import { useQuery } from "@tanstack/react-query";
import { CircleCheck, CircleX, ShieldCheck } from "lucide-react";
import { useTranslation } from "react-i18next";

import labReportApi from "@/types/clinova/labReportApi";
import query from "@/Utils/request/query";
import { formatDateTime } from "@/Utils/utils";

/**
 * Plan 28: the page the QR on a printed lab report opens. Public, no login.
 * Shows who issued and approved the report and when; never the results.
 */
export default function VerifyReport({ code }: { code: string }) {
  const { t } = useTranslation();
  const { data, isLoading, isError } = useQuery({
    queryKey: ["verify-report", code],
    queryFn: query(labReportApi.verify, {
      pathParams: { code },
      silent: true,
    }),
    retry: false,
  });

  return (
    <div className="flex min-h-screen items-center justify-center bg-gray-100 p-4">
      <div
        className="w-full max-w-md rounded-lg bg-white p-6 shadow-lg"
        data-cy="verify-report"
      >
        <div className="flex items-center gap-2 text-lg font-semibold text-gray-900">
          <ShieldCheck className="size-5 text-blue-700" />
          {t("report_verification")}
        </div>
        <div className="mt-1 text-xs text-gray-500">
          {t("verification_code")}:{" "}
          <span className="font-mono text-gray-800">{code.toUpperCase()}</span>
        </div>

        {isLoading && (
          <div className="mt-6 text-sm text-gray-500">{t("loading")}</div>
        )}

        {!isLoading && (isError || !data) && (
          <div
            className="mt-6 flex items-start gap-3 rounded-md border border-red-200 bg-red-50 p-4 text-sm text-red-800"
            data-cy="verify-not-found"
          >
            <CircleX className="mt-0.5 size-5 shrink-0" />
            <div>{t("report_not_found")}</div>
          </div>
        )}

        {data && (
          <div className="mt-6" data-cy="verify-found">
            <div
              className={
                data.valid
                  ? "flex items-start gap-3 rounded-md border border-green-200 bg-green-50 p-4 text-sm text-green-900"
                  : "flex items-start gap-3 rounded-md border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900"
              }
            >
              {data.valid ? (
                <CircleCheck className="mt-0.5 size-5 shrink-0" />
              ) : (
                <CircleX className="mt-0.5 size-5 shrink-0" />
              )}
              <div>
                <div className="font-semibold">
                  {data.valid ? t("report_verified") : t("report_not_approved")}
                </div>
                <div className="mt-1">
                  {t("report_verified_hint", { facility: data.facility })}
                </div>
              </div>
            </div>
            <dl className="mt-4 grid grid-cols-[8rem_1fr] gap-y-2 text-sm">
              <dt className="text-gray-500">{t("facility")}</dt>
              <dd className="font-medium text-gray-900">{data.facility}</dd>
              <dt className="text-gray-500">{t("verified_test")}</dt>
              <dd className="font-medium text-gray-900">{data.test}</dd>
              <dt className="text-gray-500">{t("patient_initials")}</dt>
              <dd className="font-medium text-gray-900">
                {data.patient_initials}
              </dd>
              <dt className="text-gray-500">{t("approved_by")}</dt>
              <dd className="font-medium text-gray-900">
                {data.approved_by || "—"}
                {(data.approver_qualification || data.approver_designation) && (
                  <div className="text-xs font-normal text-gray-600">
                    {[data.approver_qualification, data.approver_designation]
                      .filter(Boolean)
                      .join(" · ")}
                  </div>
                )}
              </dd>
              <dt className="text-gray-500">{t("approved_on")}</dt>
              <dd className="font-medium text-gray-900">
                {data.approved_at ? formatDateTime(data.approved_at) : "—"}
              </dd>
            </dl>
          </div>
        )}
      </div>
    </div>
  );
}
